import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const GREEN = '#1a7a4c';
const ORANGE = '#b35c00';
const RED = '#b32d2d';

const STATUS_STYLES = {
  draft:     { bg: '#eee', fg: ASHEN },
  placed:    { bg: '#e6eefc', fg: '#2a5bd7' },
  shipped:   { bg: '#fff3e6', fg: ORANGE },
  received:  { bg: '#e8f5ef', fg: GREEN },
  closed:    { bg: '#e8f5ef', fg: GREEN },
  cancelled: { bg: '#fce8e8', fg: RED },
};

const fmt = (n, ccy) => `${ccy || ''} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim();
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      textTransform: 'uppercase', letterSpacing: '0.06em', background: s.bg, color: s.fg,
    }}>{status}</span>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: '100%',
        maxWidth: wide ? 760 : 460, maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: COAL }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: ASHEN }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: ASHEN, marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, boxSizing: 'border-box' };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' });

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, fRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/factories'),
      ]);
      setPos(poRes.data.purchase_orders || []);
      setFactories(fRes.data.factories || []);
    } catch (e) {
      setError('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/purchase-orders/${id}`);
      setDetail(res.data);
    } catch (e) { setError('Failed to load PO'); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: GARNET }}>Purchase Orders</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowImport(true)} style={btn(COAL)}>⬆ Import Excel/CSV</button>
          <button onClick={() => setShowCreate(true)} style={btn(GARNET)}>+ New PO</button>
        </div>
      </div>
      <p style={{ color: ASHEN, fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        Factory orders, per-batch unit cost, and payment-float tracking. Costs/quantities entered in each factory's native unit (units for flatback, pairs for hoops).
      </p>

      {error && <div style={{ background: '#fce8e8', color: RED, padding: 10, borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {loading ? <p style={{ color: ASHEN }}>Loading…</p> : (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#faf8f8', textAlign: 'left', color: ASHEN, fontSize: 12 }}>
                <th style={{ padding: '12px 16px' }}>Reference</th>
                <th style={{ padding: '12px 16px' }}>Factory</th>
                <th style={{ padding: '12px 16px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}>Order date</th>
                <th style={{ padding: '12px 16px' }}>Balance due</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>Total cost</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>Received</th>
              </tr>
            </thead>
            <tbody>
              {pos.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: ASHEN }}>No purchase orders yet. Create one or import a spreadsheet to begin.</td></tr>
              )}
              {pos.map((po) => (
                <tr key={po.id} onClick={() => openDetail(po.id)} style={{ borderTop: '1px solid #f0eded', cursor: 'pointer' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: COAL }}>{po.reference || <span style={{ color: ASHEN }}>(no ref)</span>}</td>
                  <td style={{ padding: '12px 16px' }}>{po.factory_key}</td>
                  <td style={{ padding: '12px 16px' }}><StatusPill status={po.status} /></td>
                  <td style={{ padding: '12px 16px' }}>{fmtDate(po.order_date)}</td>
                  <td style={{ padding: '12px 16px' }}>{fmtDate(po.balance_due_date)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>{fmt(po.total_cost, po.currency)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: ASHEN }}>{po.units_received}/{po.units_ordered}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreatePOModal factories={factories} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} setError={setError} />}
      {showImport && <ImportPOModal factories={factories} onClose={() => setShowImport(false)} onSaved={() => { setShowImport(false); load(); }} setError={setError} />}
      {detail && <DetailModal po={detail} onClose={() => setDetail(null)} onChanged={() => { openDetail(detail.id); load(); }} setError={setError} />}
    </div>
  );
}

function CreatePOModal({ factories, onClose, onSaved, setError }) {
  const [factoryKey, setFactoryKey] = useState(factories[0]?.key || '');
  const [reference, setReference] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [country, setCountry] = useState('HK');
  const [currency, setCurrency] = useState('USD');
  const [lines, setLines] = useState([{ sku: '', product_title: '', qty: '', unit_cost: '' }]);
  const [saving, setSaving] = useState(false);

  const factory = factories.find((f) => f.key === factoryKey);
  const unitWord = factory?.order_unit === 'pair' ? 'pairs' : 'units';
  const costWord = factory?.order_unit === 'pair' ? 'cost / pair' : 'cost / unit';

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines((ls) => [...ls, { sku: '', product_title: '', qty: '', unit_cost: '' }]);
  const rmLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/purchase-orders', {
        factory_key: factoryKey, reference, order_date: orderDate, country, currency,
        lines: lines.filter((l) => l.sku && l.qty && l.unit_cost),
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create PO');
      setSaving(false);
    }
  };

  return (
    <Modal title="New Purchase Order" onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Factory">
          <select value={factoryKey} onChange={(e) => setFactoryKey(e.target.value)} style={inputStyle}>
            {factories.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
          </select>
        </Field>
        <Field label="Reference / PO #"><input value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} /></Field>
        <Field label="Order date"><input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} style={inputStyle} /></Field>
        <Field label="Destination">
          <select value={country} onChange={(e) => setCountry(e.target.value)} style={inputStyle}>
            <option value="HK">HK</option><option value="SG">SG</option>
          </select>
        </Field>
        <Field label="Currency"><input value={currency} onChange={(e) => setCurrency(e.target.value)} style={inputStyle} /></Field>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: ASHEN, margin: '8px 0' }}>
        Lines — quantities in {unitWord}{factory?.order_unit === 'pair' ? ' (stored internally as individual units)' : ''}
      </div>
      {lines.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 0.8fr 0.9fr 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <input placeholder="SKU" value={l.sku} onChange={(e) => setLine(i, 'sku', e.target.value)} style={inputStyle} />
          <input placeholder="Product (optional)" value={l.product_title} onChange={(e) => setLine(i, 'product_title', e.target.value)} style={inputStyle} />
          <input placeholder={unitWord} type="number" value={l.qty} onChange={(e) => setLine(i, 'qty', e.target.value)} style={inputStyle} />
          <input placeholder={costWord} type="number" value={l.unit_cost} onChange={(e) => setLine(i, 'unit_cost', e.target.value)} style={inputStyle} />
          <button onClick={() => rmLine(i)} style={{ background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>
      ))}
      <button onClick={addLine} style={{ background: 'none', border: `1px dashed ${ASHEN}`, color: ASHEN, borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', marginBottom: 16 }}>+ Add line</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f0eded', paddingTop: 14 }}>
        <div style={{ fontSize: 14, color: COAL }}>Total cost: <strong>{fmt(total, currency)}</strong> <span style={{ color: ASHEN, fontSize: 12 }}>· deposit {Math.round((factory?.deposit_pct || 0.3) * 100)}% = {fmt(total * (factory?.deposit_pct || 0.3), currency)}</span></div>
        <button onClick={save} disabled={saving} style={btn(GARNET)}>{saving ? 'Saving…' : 'Create PO'}</button>
      </div>
    </Modal>
  );
}

function ImportPOModal({ factories, onClose, onSaved, setError }) {
  const [factoryKey, setFactoryKey] = useState(factories[0]?.key || '');
  const [reference, setReference] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [country, setCountry] = useState('HK');
  const [currency, setCurrency] = useState('USD');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!file) { setError('Choose a file first'); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('factory_key', factoryKey);
    fd.append('reference', reference);
    fd.append('order_date', orderDate);
    fd.append('country', country);
    fd.append('currency', currency);
    try {
      // Let the browser set multipart/form-data WITH its boundary — setting the
      // header manually (no boundary) makes Flask unable to parse the upload.
      await api.post('/purchase-orders/import', fd, { headers: { 'Content-Type': undefined } });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Import failed');
      setSaving(false);
    }
  };

  return (
    <Modal title="Import Purchase Order" onClose={onClose}>
      <p style={{ fontSize: 13, color: ASHEN, marginTop: 0 }}>
        Upload a <strong>.xlsx</strong> or <strong>.csv</strong> with columns: <code>sku</code>, <code>qty</code>, <code>unit_cost</code>, and optionally <code>product_title</code>.
        Quantities are read in the selected factory's native unit.
      </p>
      <Field label="Factory">
        <select value={factoryKey} onChange={(e) => setFactoryKey(e.target.value)} style={inputStyle}>
          {factories.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
        </select>
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Reference / PO #"><input value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} /></Field>
        <Field label="Order date"><input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} style={inputStyle} /></Field>
        <Field label="Destination">
          <select value={country} onChange={(e) => setCountry(e.target.value)} style={inputStyle}>
            <option value="HK">HK</option><option value="SG">SG</option>
          </select>
        </Field>
        <Field label="Currency"><input value={currency} onChange={(e) => setCurrency(e.target.value)} style={inputStyle} /></Field>
      </div>
      <Field label="File">
        <input type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files[0])} style={inputStyle} />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={save} disabled={saving} style={btn(GARNET)}>{saving ? 'Importing…' : 'Import'}</button>
      </div>
    </Modal>
  );
}

function DetailModal({ po, onClose, onChanged, setError }) {
  const [receiving, setReceiving] = useState(false);

  const setStatus = async (status, extra = {}) => {
    try {
      await api.patch(`/purchase-orders/${po.id}`, { status, ...extra });
      onChanged();
    } catch (e) { setError(e.response?.data?.error || 'Update failed'); }
  };

  const receiveAll = async () => {
    setReceiving(true);
    try {
      await api.post(`/purchase-orders/${po.id}/receive`, {
        arrival_actual: new Date().toISOString().slice(0, 10),
        lines: po.lines.map((l) => ({ line_id: l.id, qty_received: l.qty_ordered - l.qty_received })),
      });
      onChanged();
    } catch (e) { setError(e.response?.data?.error || 'Receive failed'); }
    finally { setReceiving(false); }
  };

  return (
    <Modal title={`PO ${po.reference || ''} — ${po.factory_key}`} onClose={onClose} wide>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <StatusPill status={po.status} />
        <span style={{ color: ASHEN, fontSize: 13 }}>{po.country} · {po.currency}</span>
      </div>

      {/* Payment / float schedule */}
      <div style={{ background: '#faf8f8', borderRadius: 10, padding: 16, marginBottom: 18, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <Stat label="Total cost" value={fmt(po.total_cost, po.currency)} />
        <Stat label={`Deposit (${Math.round((po.deposit_pct || 0) * 100)}%)`} value={fmt(po.deposit_amount, po.currency)} sub={po.deposit_paid_at ? `paid ${fmtDate(po.deposit_paid_at)}` : 'unpaid'} />
        <Stat label="Balance (70%)" value={fmt(po.balance_amount, po.currency)} sub={po.balance_paid_at ? `paid ${fmtDate(po.balance_paid_at)}` : 'unpaid'} />
        <Stat label="Balance due" value={fmtDate(po.balance_due_date)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18, fontSize: 13, color: ASHEN }}>
        <div>Order: <strong style={{ color: COAL }}>{fmtDate(po.order_date)}</strong></div>
        <div>Ship (exp): <strong style={{ color: COAL }}>{fmtDate(po.ship_date_expected)}</strong>{po.ship_date_actual ? ` · act ${fmtDate(po.ship_date_actual)}` : ''}</div>
        <div>Arrival (exp): <strong style={{ color: COAL }}>{fmtDate(po.arrival_expected)}</strong>{po.arrival_actual ? ` · act ${fmtDate(po.arrival_actual)}` : ''}</div>
        <div>Net terms: <strong style={{ color: COAL }}>{po.balance_net_days}d</strong></div>
      </div>

      {/* Lines */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
        <thead><tr style={{ textAlign: 'left', color: ASHEN, fontSize: 11 }}>
          <th style={{ padding: '6px 8px' }}>SKU</th><th style={{ padding: '6px 8px' }}>Product</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Ordered</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Received</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Unit cost</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Line cost</th>
        </tr></thead>
        <tbody>
          {po.lines.map((l) => (
            <tr key={l.id} style={{ borderTop: '1px solid #f0eded' }}>
              <td style={{ padding: '6px 8px', fontWeight: 600 }}>{l.sku}</td>
              <td style={{ padding: '6px 8px', color: ASHEN }}>{l.product_title || '—'}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{l.native_qty}{l.order_unit === 'pair' ? ' pr' : ''}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right', color: l.qty_received >= l.qty_ordered ? GREEN : ASHEN }}>{l.qty_received}/{l.qty_ordered} u</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(l.unit_cost, po.currency)}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(l.line_cost, po.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #f0eded', paddingTop: 14 }}>
        {po.status === 'draft' && <button onClick={() => setStatus('placed', { deposit_paid_at: new Date().toISOString().slice(0, 10) })} style={btn('#2a5bd7')}>Mark placed (deposit paid)</button>}
        {po.status === 'placed' && <button onClick={() => setStatus('shipped', { ship_date_actual: new Date().toISOString().slice(0, 10) })} style={btn(ORANGE)}>Mark shipped</button>}
        {(po.status === 'placed' || po.status === 'shipped') && <button onClick={receiveAll} disabled={receiving} style={btn(GREEN)}>{receiving ? 'Receiving…' : 'Receive all'}</button>}
        {po.status === 'received' && !po.balance_paid_at && <button onClick={() => setStatus('received', { balance_paid_at: new Date().toISOString().slice(0, 10) })} style={btn(COAL)}>Mark balance paid</button>}
        {po.status !== 'cancelled' && po.status !== 'received' && <button onClick={() => setStatus('cancelled')} style={{ ...btn('#fff'), color: RED, border: `1px solid ${RED}` }}>Cancel</button>}
      </div>
    </Modal>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: ASHEN, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: COAL }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: ASHEN }}>{sub}</div>}
    </div>
  );
}
